// ════════════════════════════════════════════════════════════════════
// ILLEGAL EMPIRE — ACHIEVEMENT WRITE PATH  (progression slice)
// ────────────────────────────────────────────────────────────────────
// The Firebase write/read layer for achievements. This is the twin of the
// cosmetic grant slice in progression.js — append-only, namespaced,
// permanent provenance, idempotent. Same engine, same safety guarantees.
//
// ── HOW TO INTEGRATE ─────────────────────────────────────────────────
// Two options, both fine:
//   A) Paste this whole block at the END of progression.js (after the
//      cosmetics slice). Then delete the three import lines below — db and
//      the firebase helpers are already imported at the top of that file.
//   B) Keep it as its own module `achievementWrites.js` and import from it.
//      Leave the imports as-is.
//
// Either way the storage path is profiles/{uid}/achievements/{id}, sitting
// right beside unlockedCosmetics — Cloudflare-verifiable, shared across
// every IE game on the terror-tcg Firebase project.
//
// ── DATA SHAPE (per achievement) ─────────────────────────────────────
//   profiles/{uid}/achievements/{id} = {
//     earnedAt: <ms timestamp>,   // permanent — never overwritten
//     game:     "terror",          // origin namespace (audit/provenance)
//     rarity:   "legendary",       // snapshot of rarity at earn time
//     source:   "terror:beat_jynxi:storyMode", // where/why it fired
//   }
//
// Append-only: re-granting an owned achievement is a no-op. earnedAt and
// source are permanent — provenance never changes. This is the same
// guarantee that protects collections and cosmetics from silent resets.
// ════════════════════════════════════════════════════════════════════

// ⚠ `set` IS GONE FROM THIS LIST ON PURPOSE. Both grant paths write
// through `_claim`'s runTransaction now and nothing in this file calls
// `set` any more — and an unused import is not cosmetic here: CRA
// promotes no-unused-vars to an ERROR under CI=true, so leaving it in
// would have broken the production build of all five apps that carry
// this file.
import { ref, get, onValue, runTransaction } from "firebase/database";
import { db } from "./firebase";
import {
  ACHIEVEMENTS,
  isValidAchievement,
  getAchievement,
  deriveMetaAchievements,
  crestsFor,
} from "./achievements";
import { grantCrests } from "./ieEconomy";

// ════════════════════════════════════════════════════════════════════
// ◆ THE ACHIEVEMENT FAUCET — Empire-wide, 2026-08-20
// ════════════════════════════════════════════════════════════════════
// caP'n, 2026-08-20, asked where Divine Crests come from and named
// achievements first. This is that, and it belongs in the KIT rather
// than in any one title, for the same reason the ranks do: an
// achievement is worth the same everywhere or the ladder means nothing.
//
// ⭐ THE PAYOUT IS NEVER DECIDED HERE. achievements.js already prices
// every id — crestsFor(id) → 1 / 3 / 5 by THE THREE RANKS (Beb, Champ,
// Lord) — and its own banner says a call site that hardcodes a Crest
// amount is a bug even when the number happens to be right today. So
// this reads the price and pays it.
//
// ⭐ AND IT HANGS HERE BECAUSE THIS IS THE ONLY PLACE THAT KNOWS
// "NEWLY EARNED". Every grant call site in every title funnels through
// grantAchievement, and the `snap.exists()` branch above is the single
// point where a fresh earn is distinguishable from a re-grant. Paying
// at any call site instead would mean ~30 edits in Terror alone and a
// double-pay the first time one of them forgot the check.
//
// Non-fatal by construction: an achievement is a permanent record and a
// currency hiccup must never cost the player one. The stamp is already
// down before this runs.
// ⭐ CLAIM AN ACHIEVEMENT ATOMICALLY. Returns true only for the writer
// that actually created the record.
//
// ⚠ WHY THIS EXISTS. Both grant paths below used to be `get()` → check
// `exists()` → `set()`, which is a read-then-write with a window in it.
// That was survivable while an achievement was only a badge; the moment
// it PAYS DIVINE CRESTS it is a money write, and profiles/{uid}/
// achievements is shared across every IE title on the same Firebase
// project. Two tabs — Terror in one, Pierre in the other — both derive
// `empire:citizen`, both see it absent, both write it, and both pay.
// worldData.stamp() has used exactly this transaction shape for the
// world map's rewards all along; the achievement faucet was the one
// currency path in the Empire whose idempotency guard was a bare read.
//
// `undefined` from the updater ABORTS the transaction, which is how
// "already earned" is reported without touching the existing record —
// so earnedAt and source stay permanent, exactly as before.
const _claim = async (uid, id, record) => {
  const r = ref(db, `profiles/${uid}/achievements/${id}`);
  const res = await runTransaction(r, (cur) => (cur ? undefined : record));
  return !!res.committed;
};

const _payCrests = async (uid, ids) => {
  for (const id of ids) {
    try {
      const amt = crestsFor(id);
      // eslint-disable-next-line no-await-in-loop
      if (amt > 0) await grantCrests(uid, amt, id);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[achievements] Crest payout failed for", id, e && e.message);
    }
  }
};

// ── GRANT ─────────────────────────────────────────────────────────────
// Award one achievement. Append-only & idempotent: if already earned, the
// original record is preserved untouched and this returns alreadyEarned.
//
// After a successful grant, this AUTOMATICALLY checks the empire meta tier
// (Two Worlds, Citizen) and grants any newly-qualified meta achievements
// too — so the universe acknowledges itself without every call site
// having to remember to do it.
//
// source: optional extra provenance string (e.g. "storyMode:chapter3").
//         The achievement's own id is always recorded as the base source.
//
// Returns: {
//   success, alreadyEarned, reason?,
//   granted: [ids...],   // everything newly written this call (base + meta)
// }
export const grantAchievement = async (uid, achievementId, source = "") => {
  if (!uid || !achievementId) {
    return { success: false, reason: "Missing uid or achievementId.", granted: [] };
  }
  if (!isValidAchievement(achievementId)) {
    return { success: false, reason: `Unknown achievement: ${achievementId}`, granted: [] };
  }

  const def = getAchievement(achievementId);

  // Append-only and ATOMIC — see _claim. A losing racer gets `false`
  // here and pays nothing, while still running the meta check below in
  // case earlier grants left the player newly qualified for a meta
  // achievement they do not yet hold.
  const won = await _claim(uid, achievementId, {
    earnedAt: Date.now(),
    game: def.game,
    rarity: def.rarity,
    source: source ? `${achievementId}:${source}` : achievementId,
  });

  const metaGranted = await _grantMeta(uid);
  await _payCrests(uid, won ? [achievementId, ...metaGranted] : metaGranted);

  return {
    success: true,
    alreadyEarned: !won,
    granted: won ? [achievementId, ...metaGranted] : metaGranted,
  };
};

// Internal: read the player's full achievement map, derive which empire
// meta achievements they now qualify for, and write any missing ones.
// Idempotent and safe to call repeatedly. Returns ids newly written.
const _grantMeta = async (uid) => {
  const map = await getAchievements(uid);
  const toGrant = deriveMetaAchievements(map);
  const written = [];
  for (const id of toGrant) {
    const def = getAchievement(id);
    if (!def) continue;
    // "someone beat us to it" was a get()-then-set(), which is exactly
    // the race it claims to guard against. It is a transaction now —
    // see _claim.
    // eslint-disable-next-line no-await-in-loop
    const won = await _claim(uid, id, {
      earnedAt: Date.now(),
      game: def.game,
      rarity: def.rarity,
      source: `${id}:auto`,
    });
    if (won) written.push(id);
  }
  return written;
};

// ── READ ──────────────────────────────────────────────────────────────

// One-shot read of the full achievement map.
// Shape: { [id]: { earnedAt, game, rarity, source } }
export const getAchievements = async (uid) => {
  if (!uid) return {};
  const snap = await get(ref(db, `profiles/${uid}/achievements`));
  return snap.val() || {};
};

// Live listener for the achievement map. Fires with the full map on every
// change. Returns the unsubscribe function. (Twin of listenToUnlocked.)
export const listenToAchievements = (uid, callback) => {
  if (!uid) {
    callback({});
    return () => {};
  }
  return onValue(ref(db, `profiles/${uid}/achievements`), (snap) => {
    callback(snap.val() || {});
  });
};

// Quick check: does this uid hold this achievement?
export const hasAchievement = async (uid, achievementId) => {
  if (!uid || !achievementId) return false;
  const snap = await get(ref(db, `profiles/${uid}/achievements/${achievementId}`));
  return snap.exists();
};

// ── CROSS-GAME GATE (the Cloudflare-verifiable check) ─────────────────
// Convenience wrapper: fetch the player's achievements and evaluate a
// reward gate in one call. Most UI should prefer the pure isRewardUnlocked
// from achievements.js (no Firebase round-trip) when it already holds the
// map via the hook — but this exists for one-shot server-style checks.
export const checkRewardUnlocked = async (uid, rewardId) => {
  const { isRewardUnlocked } = await import("./achievements");
  const map = await getAchievements(uid);
  return isRewardUnlocked(map, rewardId);
};

// ════════════════════════════════════════════════════════════════════
// END OF IE ACHIEVEMENTS — SHARED WRITE PATH
// ════════════════════════════════════════════════════════════════════
